import type {ReactElement} from 'react';
import React from 'react';
import type {UserMessageProps} from './user-message.types';
import styles from './user-message.module.scss';
import {Image} from 'react-bootstrap';
import {getImageScreenURL} from '@Libraries/image-item.library';
import {UserImageSource} from '@Libraries/user-image-library';
import MarkdownRenderer from '@Components/ai-chat-window/components/markdown-renderer/markdown-renderer';
import {getPreviewURL, PosterPreviewExtension} from '@Libraries/poster-library';
import type {ChatMessageDesignPart, ChatMessageUserAttachment, ChatMessageUserImagePart} from '@Components/ai-chat-window/ai-chat-window.types';

export function UserMessageRow({...props}: UserMessageProps): ReactElement {
  const getUserImageAttachment = (part: ChatMessageUserImagePart, partKey: string): ReactElement => {
    return (
      <Image
        loading={'lazy'}
        key={partKey}
        src={getImageScreenURL(part.hashedFilename, 'png', UserImageSource.UPLOAD)}
        className={styles.chatPromptAttachment}
        alt="User attachments"
      />
    );
  };

  const getUserDesignAttachment = (part: ChatMessageDesignPart, partKey: string): ReactElement => {
    return <Image loading={'lazy'} key={partKey} src={getPreviewURL(part.hashedId, PosterPreviewExtension.IMAGE)} className={styles.chatPromptAttachment} alt="User attachments" />;
  };

  const getAttachmentParts = (attachmentParts: ChatMessageUserAttachment[]): ReactElement => {
    return (
      <>
        {attachmentParts.map((part, index) => {
          const partKey = `user-part-${index}`;
          if (part.type === 'user_image') {
            return getUserImageAttachment(part, partKey);
          }
          if (part.type === 'design') {
            return getUserDesignAttachment(part, partKey);
          }
          return null;
        })}
      </>
    );
  };

  const getAttachments = (attachmentParts: ChatMessageUserAttachment[]): ReactElement => {
    return (
      <div className="flex-justify-end spacing-m-b-1">
        {getAttachmentParts(attachmentParts)}
      </div>
    );
  };

  const getUserMessage = (): ReactElement => {
    const parts = props.message.parts ?? [];
    const textParts = parts.filter((part) => part.type === 'text');
    const attachmentParts = parts.filter((part) => part.type === 'user_image' || part.type === 'design');

    return (
      <div className={styles.userMessage}>
        {getAttachments(attachmentParts)}
        {textParts.map((part, index) => (
          <div key={`user-text-part-${index}`} className={`${props.bubbleClassNames} ${styles.userText}`}>
            <MarkdownRenderer content={part.text ?? ''} />
          </div>
        ))}
      </div>
    );
  };

  return getUserMessage();
}
